import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import Card from '../../../shared/components/Card';
import { getApiBaseUrl } from '../../../core/config/api';
import { useInterventionsList } from './hooks/useInterventionsList';
import configService from '../../../shared/services/configService';
import type { Service, VehiculeConfig } from '../../../shared/types/config';

export interface CategorieMission {
  id: number;
  nom: string;
  label?: string;
}

/** Conditions d’unlock définies sur le scénario (`notes_cta.panel_meta.conditions_spec`). */
export interface InterventionConditionsSpec {
  batiments: { config_building_id: number; quantite_min: number }[];
  /** Réservé rétrocompat / anciennes données ; le formulaire n’envoie plus de compétences ici. */
  competence_ids?: number[];
}

/** Réponse GET /api/cta/interventions/:id pour préremplir le formulaire */
export interface PanelInterventionDetail {
  id: number;
  service_id: number;
  titres: string[];
  types_mission: number[];
  /** Ancien mode : IDs `cta_conditions` (optionnel si tout est en conditions_spec). */
  conditions: number[];
  conditions_spec: InterventionConditionsSpec;
  statut: string;
  is_tuto: boolean;
  is_exceptionnelle: boolean;
  params: {
    temps_max: number;
    victimes_min: number;
    victimes_max: number;
    hostiles_min: number;
    hostiles_max: number;
    vehicules_accidentes_min: number;
    vehicules_accidentes_max: number;
  };
  recompense: { xp: number; credits: number };
  evolutions: { id: number; taux: number }[];
  vehicules_requis: { id_vehicule: number; quantite: number }[];
  memo?: string;
}

const InterventionsCreate: React.FC = () => {
  const navigate = useNavigate();
  const { id: editIdParam } = useParams<{ id: string }>();
  const editingId = editIdParam ? parseInt(editIdParam, 10) : NaN;
  const isEditMode = Number.isInteger(editingId) && editingId > 0;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [categoriesMission, setCategoriesMission] = useState<CategorieMission[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [configBuildingTypes, setConfigBuildingTypes] = useState<
    Array<{ id: number; code: string; nom: string; service_id: number | null }>
  >([]);
  const [conditionBatimentRows, setConditionBatimentRows] = useState<
    { config_building_id: number; quantite_min: number }[]
  >([{ config_building_id: 0, quantite_min: 1 }]);
  /** id d'intervention → taux d'évolution en % (0-100) */
  const [evolutionTaux, setEvolutionTaux] = useState<Record<number, number>>({});
  const [services, setServices] = useState<Service[]>([]);
  const [vehicules, setVehicules] = useState<VehiculeConfig[]>([]);
  const [loadingVehicules, setLoadingVehicules] = useState(true);
  const [openServiceId, setOpenServiceId] = useState<number | null>(null);
  const [vehiculeSearchQuery, setVehiculeSearchQuery] = useState('');
  /** id véhicule (config) → quantité requise pour cette intervention (défaut 0) */
  const [vehiculeQuantites, setVehiculeQuantites] = useState<Record<number, number>>({});
  /** Tutoriel et exceptionnelle sont mutuellement exclusifs */
  const [scenarioKind, setScenarioKind] = useState<'aucun' | 'tuto' | 'exceptionnelle'>('aucun');

  const [panelEditDetail, setPanelEditDetail] = useState<PanelInterventionDetail | null>(null);
  const [loadingEditDetail, setLoadingEditDetail] = useState(false);
  const [detailLoadError, setDetailLoadError] = useState<string | null>(null);

  /** Ancien mode `cta_conditions` (IDs) — exposé pour le JSX / HMR si référencé. */
  const conditions = useMemo(
    () => (panelEditDetail?.conditions?.length ? [...panelEditDetail.conditions] : []),
    [panelEditDetail]
  );
  const { interventions: interventionsList, loading: loadingInterventions, reload: reloadInterventions } =
    useInterventionsList();
  const formRef = useRef<HTMLFormElement>(null);

  const d = isEditMode ? panelEditDetail : null;
  const formMountKey = isEditMode
    ? `edit-${editingId}-${d?.id ?? 0}-c${categoriesMission.length}-b${configBuildingTypes.length}-i${loadingInterventions ? 0 : interventionsList.length}`
    : 'create';

  useEffect(() => {
    if (!isEditMode) {
      setPanelEditDetail(null);
      setDetailLoadError(null);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setDetailLoadError('Token d\'authentification manquant');
      return;
    }
    let cancelled = false;
    const loadDetail = async () => {
      setLoadingEditDetail(true);
      setDetailLoadError(null);
      try {
        const apiUrl = getApiBaseUrl();
        const res = await fetch(`${apiUrl}/api/cta/interventions/${editingId}`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        const ct = res.headers.get('content-type');
        if (!ct || !ct.includes('application/json')) {
          if (!cancelled) setDetailLoadError('Réponse serveur invalide.');
          return;
        }
        const data = await res.json();
        if (cancelled) return;
        if (!data.success || !data.data) {
          setDetailLoadError(data.error || 'Scénario introuvable.');
          setPanelEditDetail(null);
          return;
        }
        const det = data.data as PanelInterventionDetail;
        setPanelEditDetail(det);
        const evo: Record<number, number> = {};
        for (const e of det.evolutions || []) {
          if (e.id > 0) evo[e.id] = e.taux;
        }
        setEvolutionTaux(evo);
        const vq: Record<number, number> = {};
        for (const v of det.vehicules_requis || []) {
          if (v.id_vehicule > 0) vq[v.id_vehicule] = v.quantite;
        }
        setVehiculeQuantites(vq);
        if (det.is_tuto) setScenarioKind('tuto');
        else if (det.is_exceptionnelle) setScenarioKind('exceptionnelle');
        else setScenarioKind('aucun');

        const spec = det.conditions_spec;
        if (spec?.batiments?.length) {
          setConditionBatimentRows(
            spec.batiments.map((b) => ({
              config_building_id: b.config_building_id,
              quantite_min: Math.max(1, b.quantite_min || 1),
            }))
          );
        } else {
          setConditionBatimentRows([{ config_building_id: 0, quantite_min: 1 }]);
        }
      } catch (e: any) {
        if (!cancelled) setDetailLoadError(e?.message || 'Erreur de chargement.');
      } finally {
        if (!cancelled) setLoadingEditDetail(false);
      }
    };
    void loadDetail();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, editingId]);

  const interventionsForEvolutions = useMemo(
    () => interventionsList.filter((inter) => !isEditMode || inter.id !== editingId),
    [interventionsList, isEditMode, editingId]
  );

  const canShowInterventionForm =
    !isEditMode || (!!panelEditDetail && !loadingEditDetail && !detailLoadError);

  const setVehiculeQuantite = (vehiculeId: number, quantite: number) => {
    setVehiculeQuantites((prev) => ({ ...prev, [vehiculeId]: Math.max(0, quantite) }));
  };

  /** Par service : véhicules groupés par type, triés par nom (alpha) */
  const vehiculesByService = useMemo(() => {
    return services.map((service) => {
      const list = (vehicules.filter((v) => v.id_service === service.id) as VehiculeConfig[])
        .slice()
        .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
      const byType: Record<string, VehiculeConfig[]> = {};
      list.forEach((v) => {
        const t = v.type || 'Autre';
        if (!byType[t]) byType[t] = [];
        byType[t].push(v);
      });
      const types = Object.keys(byType).sort((a, b) => a.localeCompare(b, 'fr'));
      return { service, byType, types };
    });
  }, [services, vehicules]);

  const setEvolutionTauxFor = (id: number, taux: number) => {
    setEvolutionTaux((prev) => ({ ...prev, [id]: Math.min(100, Math.max(0, taux)) }));
  };

  const toggleEvolution = (id: number, checked: boolean) => {
    setEvolutionTaux((prev) => ({ ...prev, [id]: checked ? 50 : 0 }));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const apiUrl = getApiBaseUrl();
    const headers = { Authorization: `Bearer ${token}` };

    const load = async () => {
      setLoadingOptions(true);
      try {
        const [catRes, buildingsRes] = await Promise.all([
          fetch(`${apiUrl}/api/cta/categories-mission`, { headers, credentials: 'include' }),
          fetch(`${apiUrl}/api/cta/config-building-types`, { headers, credentials: 'include' }),
        ]);

        if (catRes.ok) {
          const catData = await catRes.json();
          if (catData.success && Array.isArray(catData.data)) {
            setCategoriesMission(catData.data);
          }
        }
        if (buildingsRes.ok) {
          const bData = await buildingsRes.json();
          if (bData.success && Array.isArray(bData.data)) {
            setConfigBuildingTypes(bData.data);
          }
        }
      } catch (e) {
        console.warn('Chargement des options:', e);
      } finally {
        setLoadingOptions(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    const loadServicesVehicules = async () => {
      setLoadingVehicules(true);
      try {
        const [servRes, vehRes] = await Promise.all([
          configService.getServices(),
          configService.getVehiculeConfigs(),
        ]);
        if (servRes.success && servRes.data) setServices(servRes.data);
        if (vehRes.success && vehRes.data) setVehicules(vehRes.data);
      } catch (e) {
        console.warn('Chargement services / véhicules:', e);
      } finally {
        setLoadingVehicules(false);
      }
    };
    loadServicesVehicules();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData(e.currentTarget);
      const titre = formData.get('titre');
      const isTuto = scenarioKind === 'tuto';
      const isExceptionnelle = scenarioKind === 'exceptionnelle';
      const batimentsPayload = conditionBatimentRows
        .filter((row) => row.config_building_id > 0 && row.quantite_min >= 1)
        .map((row) => ({
          config_building_id: row.config_building_id,
          quantite_min: row.quantite_min,
        }));
      const totalVehiculesRequis = Object.values(vehiculeQuantites).reduce(
        (sum, qty) => sum + Math.max(0, qty || 0),
        0
      );
      if (totalVehiculesRequis < 1) {
        setError('Ajoutez au moins 1 véhicule requis pour débloquer/générer cet appel CTA.');
        setLoading(false);
        return;
      }
      const interventionData = {
        types_mission: formData.getAll('types_mission').map((v) => Number(v)).filter((n) => !Number.isNaN(n)),
        conditions: isEditMode ? conditions : [],
        conditions_spec: {
          batiments: batimentsPayload,
          competence_ids: [],
        },
        statut: panelEditDetail?.statut ?? 'EN_COURS',
        is_tuto: isTuto,
        is_exceptionnelle: isExceptionnelle,
        titres: titre ? [titre.toString()] : [],
        contextes_app: {
          heure_debut: formData.get('heure_debut') || '',
          heure_fin: formData.get('heure_fin') || '',
          jours_semaine: [],
          mois: [],
          meteo: '',
          heure: ''
        },
        params: {
          temps_max: parseInt(formData.get('temps_max') as string) || 15,
          moyens_min: {
            sp: [],
            samu: [],
            fdo: [],
            autres: []
          },
          victimes_min: parseInt(formData.get('victimes_min') as string) || 0,
          victimes_max: parseInt(formData.get('victimes_max') as string) || 0,
          hostiles_min: parseInt(formData.get('hostiles_min') as string) || 0,
          hostiles_max: parseInt(formData.get('hostiles_max') as string) || 0,
          vehicules_accidentes_min: parseInt(formData.get('vehicules_accidentes_min') as string) || 0,
          vehicules_accidentes_max: parseInt(formData.get('vehicules_accidentes_max') as string) || 0
        },
        recompense: {
          xp: parseInt(formData.get('recompense_xp') as string) || 0,
          credits: parseInt(formData.get('recompense_credits') as string) || 0
        },
        facteurs_dynamiques: {
          meteo: '',
          heure: '',
          circulation: '',
          evenement_local: ''
        },
        evolutions: Object.entries(evolutionTaux)
          .filter(([, taux]) => taux > 0)
          .map(([id, taux]) => ({ id: Number(id), taux })),
        vehicules_requis: Object.entries(vehiculeQuantites)
          .filter(([id, q]) => q > 0 && Number(id) > 0)
          .map(([id, q]) => ({ id_vehicule: Number(id), quantite: q })),
        messages_radio: [],
        renforts_possibles: [],
        score_si: {
          bonus: [],
          malus: []
        },
        resultats: {
          positif: '',
          neutre: '',
          negatif: ''
        },
        notes_cta: [],
        memo: '',
        ...(isEditMode && panelEditDetail?.service_id
          ? { service_id: panelEditDetail.service_id }
          : {}),
      };

      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Token d\'authentification manquant');
        setLoading(false);
        return;
      }

      const apiUrl = getApiBaseUrl();
      const saveUrl = isEditMode
        ? `${apiUrl}/api/cta/interventions/${editingId}`
        : `${apiUrl}/api/cta/interventions/create`;
      const saveMethod = isEditMode ? 'PUT' : 'POST';
      const response = await fetch(saveUrl, {
        method: saveMethod,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(interventionData)
      });

      // Vérifier si la réponse est du JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Réponse non-JSON reçue:', text.substring(0, 200));
        setError('Le serveur a retourné une réponse invalide. Vérifiez que la route existe.');
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.success) {
        if (isEditMode) {
          setSuccess('Scénario mis à jour avec succès.');
          void reloadInterventions();
        } else {
          setSuccess('Scénario d\'intervention créé avec succès. Vous pouvez en créer un autre ci-dessous.');
          formRef.current?.reset();
          setEvolutionTaux({});
          setVehiculeQuantites({});
          setScenarioKind('aucun');
          setOpenServiceId(null);
          setVehiculeSearchQuery('');
          setConditionBatimentRows([{ config_building_id: 0, quantite_min: 1 }]);
          void reloadInterventions();
        }
      } else {
        setError(data.error || (isEditMode ? 'Erreur lors de la mise à jour.' : 'Erreur lors de la création du scénario'));
      }
    } catch (err: any) {
      setError(err.message || (isEditMode ? 'Erreur lors de la mise à jour.' : 'Erreur lors de la création du scénario'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar />
      <div className="relative z-10 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold text-white mb-2 text-gradient">
                {isEditMode ? 'Modifier le scénario d\'intervention' : 'Créer un scénario d\'intervention'}
              </h1>
              <p className="text-gray-300">
                {isEditMode
                  ? `Scénario #${editingId} — mettez à jour les champs puis enregistrez.`
                  : 'Créez un nouveau scénario d\'intervention CTA'}
              </p>
            </div>
            <button
              onClick={() => navigate('/panel')}
              className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Retour</span>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-200">
              {success}
            </div>
          )}

          {isEditMode && loadingEditDetail && !detailLoadError && (
            <Card className="p-6 mb-4">
              <p className="text-gray-300">Chargement du scénario...</p>
            </Card>
          )}

          {isEditMode && detailLoadError && (
            <Card className="p-6 mb-4">
              <p className="text-red-300 mb-4">{detailLoadError}</p>
              <button
                type="button"
                onClick={() => navigate('/panel/interventions/manage')}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              >
                Retour à la liste des scénarios
              </button>
            </Card>
          )}

          {canShowInterventionForm && (
          <Card className="p-6">
            <form key={formMountKey} ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Titre *
                  </label>
                  <input
                    type="text"
                    name="titre"
                    required
                    defaultValue={d?.titres?.[0] ?? ''}
                    className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Titre de l'intervention"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Catégorie de mission
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Cette catégorie sert à classifier le scénario côté CTA (filtrage, tri et lecture rapide des scénarios par thème).
                  </p>
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800/50 p-3">
                    {loadingOptions ? (
                      <p className="text-sm text-gray-500">Chargement...</p>
                    ) : categoriesMission.length === 0 ? (
                      <p className="text-sm text-gray-500">Aucune catégorie. Créez-en dans la base de données.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {categoriesMission.map((item) => (
                          <label key={item.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-700/30 rounded px-2 py-1.5">
                            <input
                              type="checkbox"
                              name="types_mission"
                              value={item.id}
                              defaultChecked={d?.types_mission?.includes(item.id) ?? false}
                              className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-primary-600 focus:ring-2 focus:ring-primary-500"
                            />
                            <span className="text-sm text-gray-200">{item.nom || item.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2 rounded-lg border border-gray-700 bg-gray-800/40 p-4 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Conditions de déblocage</label>
                    <p className="text-xs text-gray-500 mb-3">
                      Types de bâtiments requis (quantité minimale par type). Les quantités de véhicules pour la mission
                      se règlent dans la section « Véhicules par service » ci-dessous — les compétences du personnel /
                      armement des engins restent hors intervention.
                    </p>
                    <p className="text-xs text-blue-300/90 mb-3">
                      Conseil gameplay : définissez au moins un minimum de véhicules côté mission (ex: 1 véhicule pour le tutoriel)
                      afin de débloquer le premier appel proprement.
                    </p>
                    {loadingOptions ? (
                      <p className="text-sm text-gray-500">Chargement des types de bâtiments…</p>
                    ) : (
                      <>
                        <p className="text-xs font-medium text-gray-400 mb-2">Bâtiments (quantité minimale par type)</p>
                        <div className="space-y-2">
                          {conditionBatimentRows.map((row, idx) => (
                            <div key={idx} className="flex flex-wrap items-center gap-2">
                              <select
                                value={row.config_building_id || ''}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10) || 0;
                                  setConditionBatimentRows((prev) => {
                                    const next = [...prev];
                                    next[idx] = { ...next[idx], config_building_id: v };
                                    return next;
                                  });
                                }}
                                className="flex-1 min-w-[200px] px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
                              >
                                <option value="">— Type de bâtiment —</option>
                                {configBuildingTypes.map((bt) => (
                                  <option key={bt.id} value={bt.id}>
                                    {bt.nom}
                                    {bt.code ? ` (${bt.code})` : ''}
                                  </option>
                                ))}
                              </select>
                              <label className="flex items-center gap-1 text-sm text-gray-400">
                                min.
                                <input
                                  type="number"
                                  min={1}
                                  value={row.quantite_min}
                                  onChange={(e) => {
                                    const q = parseInt(e.target.value, 10) || 1;
                                    setConditionBatimentRows((prev) => {
                                      const next = [...prev];
                                      next[idx] = { ...next[idx], quantite_min: Math.max(1, q) };
                                      return next;
                                    });
                                  }}
                                  className="w-16 px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                                />
                              </label>
                              <button
                                type="button"
                                onClick={() =>
                                  setConditionBatimentRows((prev) => prev.filter((_, i) => i !== idx))
                                }
                                disabled={conditionBatimentRows.length <= 1}
                                className="text-xs text-red-400 hover:text-red-300 disabled:opacity-40 px-2"
                              >
                                Retirer
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setConditionBatimentRows((prev) => [
                              ...prev,
                              { config_building_id: 0, quantite_min: 1 },
                            ])
                          }
                          className="mt-2 text-sm text-primary-400 hover:text-primary-300"
                        >
                          + Ajouter un type de bâtiment
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Évolutions de mission
                  </label>
                  <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800/50 p-3">
                    {loadingInterventions ? (
                      <p className="text-sm text-gray-500">Chargement de la liste des interventions...</p>
                    ) : interventionsForEvolutions.length === 0 ? (
                      <p className="text-sm text-gray-500">Aucune intervention existante. Créez d’abord des scénarios (liste du panel) pour pouvoir les choisir comme évolution.</p>
                    ) : (
                      <div className="space-y-2">
                        {interventionsForEvolutions.map((inter) => {
                          const checked = (evolutionTaux[inter.id] ?? 0) > 0;
                          const taux = evolutionTaux[inter.id] ?? 50;
                          return (
                            <div key={inter.id} className="flex items-center gap-3 rounded px-2 py-1.5 hover:bg-gray-700/30">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => toggleEvolution(inter.id, e.target.checked)}
                                className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-primary-600 focus:ring-2 focus:ring-primary-500 shrink-0"
                              />
                              <span className="text-sm text-gray-200 flex-1 min-w-0 truncate">
                                {inter.titres?.[0] || inter.categorie || `Intervention #${inter.id}`}
                              </span>
                              {checked && (
                                <div className="flex items-center gap-1 shrink-0">
                                  <input
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={taux}
                                    onChange={(e) => setEvolutionTauxFor(inter.id, parseInt(e.target.value, 10) || 0)}
                                    className="w-14 px-2 py-1 text-sm bg-gray-800/50 border border-gray-600 rounded text-white focus:ring-1 focus:ring-primary-500"
                                  />
                                  <span className="text-xs text-gray-400">%</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Cochez les scénarios vers lesquels cette intervention peut évoluer et indiquez le taux de chance (%).</p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Véhicules par service
                  </label>
                  {loadingVehicules ? (
                    <p className="text-sm text-gray-500">Chargement des services et véhicules...</p>
                  ) : vehiculesByService.length === 0 ? (
                    <p className="text-sm text-gray-500">Aucun service ou véhicule configuré.</p>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={vehiculeSearchQuery}
                        onChange={(e) => setVehiculeSearchQuery(e.target.value)}
                        placeholder="Rechercher un véhicule ou un type..."
                        className="w-full mb-3 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <div className="space-y-2 rounded-lg border border-gray-700 overflow-hidden">
                        {vehiculesByService.map(({ service, byType, types }) => {
                          const searchLower = vehiculeSearchQuery.trim().toLowerCase();
                          const filterV = (list: VehiculeConfig[]) =>
                            !searchLower
                              ? list
                              : list.filter(
                                  (v) =>
                                    v.nom.toLowerCase().includes(searchLower) ||
                                    (v.type || '').toLowerCase().includes(searchLower)
                                );
                          const typesFiltered = types.filter((t) => filterV(byType[t] || []).length > 0);
                          const count = typesFiltered.reduce((acc, t) => acc + filterV(byType[t] || []).length, 0);
                          if (count === 0) return null;
                          const isOpen = openServiceId === service.id;
                          return (
                            <div key={service.id} className="bg-gray-800/50">
                              <button
                                type="button"
                                onClick={() => setOpenServiceId(isOpen ? null : service.id)}
                                className="w-full flex items-center justify-between px-4 py-3 text-left text-gray-200 hover:bg-gray-700/50 transition-colors"
                              >
                                <span className="font-medium">{service.nom}</span>
                                <span className="text-sm text-gray-400">
                                  {count} véhicule{count !== 1 ? 's' : ''}
                                </span>
                                <svg
                                  className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {isOpen && (
                                <div className="border-t border-gray-700 px-4 py-3 space-y-4">
                                  {typesFiltered.map((type) => {
                                    const list = filterV(byType[type] || []);
                                    return (
                                      <div key={type}>
                                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                                          {type}
                                        </div>
                                        <ul className="space-y-2">
                                          {list.map((v, i) => (
                                            <li key={v.id ?? i} className="flex items-center justify-between gap-3 text-sm text-gray-200">
                                              <span className="min-w-0 truncate">{v.nom}</span>
                                              <input
                                                type="number"
                                                min={0}
                                                value={vehiculeQuantites[v.id ?? 0] ?? 0}
                                                onChange={(e) => setVehiculeQuantite(v.id ?? 0, parseInt(e.target.value, 10) || 0)}
                                                className="w-20 px-2 py-1 text-sm bg-gray-800/50 border border-gray-600 rounded text-white focus:ring-1 focus:ring-primary-500 shrink-0"
                                              />
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Quantité requise pour cette intervention (0 par défaut).</p>
                    </>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Temps max (minutes)
                  </label>
                  <input
                    type="number"
                    name="temps_max"
                    defaultValue={d?.params?.temps_max ?? 15}
                    min={1}
                    className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Victimes (min)</label>
                  <input
                    type="number"
                    name="victimes_min"
                    defaultValue={d?.params?.victimes_min ?? 0}
                    min={0}
                    className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Victimes (max)</label>
                  <input
                    type="number"
                    name="victimes_max"
                    defaultValue={d?.params?.victimes_max ?? 0}
                    min={0}
                    className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Hostiles (min)</label>
                  <input
                    type="number"
                    name="hostiles_min"
                    defaultValue={d?.params?.hostiles_min ?? 0}
                    min={0}
                    className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Hostiles (max)</label>
                  <input
                    type="number"
                    name="hostiles_max"
                    defaultValue={d?.params?.hostiles_max ?? 0}
                    min={0}
                    className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Véhicules accidentés (min)</label>
                  <input
                    type="number"
                    name="vehicules_accidentes_min"
                    defaultValue={d?.params?.vehicules_accidentes_min ?? 0}
                    min={0}
                    className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Véhicules accidentés (max)</label>
                  <input
                    type="number"
                    name="vehicules_accidentes_max"
                    defaultValue={d?.params?.vehicules_accidentes_max ?? 0}
                    min={0}
                    className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div className="md:col-span-2 border-t border-gray-700 pt-4 mt-2">
                  <label className="block text-sm font-medium text-gray-300 mb-3">Récompense de l'intervention</label>
                  <p className="text-xs text-gray-500 mb-3">XP et € accordés une fois l'intervention terminée.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">XP</label>
                      <input
                        type="number"
                        name="recompense_xp"
                        defaultValue={d?.recompense?.xp ?? 0}
                        min={0}
                        className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">€</label>
                      <input
                        type="number"
                        name="recompense_credits"
                        defaultValue={d?.recompense?.credits ?? 0}
                        min={0}
                        className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 space-y-3">
                  <p className="text-xs text-gray-500">
                    Type de scénario (optionnel) : choisir au plus une option — tutoriel ou exceptionnelle, pas les deux.
                  </p>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scenarioKind === 'tuto'}
                      onChange={(e) => setScenarioKind(e.target.checked ? 'tuto' : 'aucun')}
                      className="w-5 h-5 rounded bg-gray-800/50 border-gray-700 text-primary-600 focus:ring-2 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-300">
                      Intervention tutoriel
                    </span>
                    <span className="text-xs text-gray-400">
                      (Destinée à l&apos;apprentissage)
                    </span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={scenarioKind === 'exceptionnelle'}
                      onChange={(e) => setScenarioKind(e.target.checked ? 'exceptionnelle' : 'aucun')}
                      className="w-5 h-5 rounded bg-gray-800/50 border-gray-700 text-primary-600 focus:ring-2 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-300">
                      Intervention exceptionnelle
                    </span>
                    <span className="text-xs text-gray-400">
                      (Événement rare ou hors routine)
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/panel')}
                  className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-700 hover:to-purple-700 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (isEditMode ? 'Enregistrement...' : 'Création...') : isEditMode ? 'Enregistrer les modifications' : 'Créer le scénario'}
                </button>
              </div>
            </form>
          </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterventionsCreate;

